import Button from '@pelckmans/elementis/components/button';
import Dialog from '@pelckmans/elementis/components/dialog';
import SearchField from '@pelckmans/elementis/components/search-field';
import { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ModalContext from '../../../../../contexts/ModalContext';
import List from '@pelckmans/elementis/components/list';
import NodeListItemGroup, { RowGroup } from './NodeListItemGroup';
import useScorableItemFilters, { ResultType } from './hooks/useScorableItemFilters';
import Empty from './Empty';
import LocalizedDateField from '../../../../../shared-components/elementis/components/localized-date-field';
import Checkbox from '@pelckmans/elementis/components/checkbox';
import { DateRange } from 'react-day-picker';
import Alert from '@pelckmans/elementis/components/alert';

const dateFormatter = Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: '2-digit',
});

export type FilteredItem =
  | {
      type: 'node';
      id: string;
    }
  | {
      type: 'scorable-item';
      id: string;
    };

interface Props {
  id: string;
  nodeRowGroups: RowGroup[];
  onConfirm: (filteredItems: FilteredItem[]) => void;
}

export default function ScorableItemFilterModal({ id, nodeRowGroups, onConfirm }: Props) {
  const { t } = useTranslation();
  const { closeModal } = useContext(ModalContext);
  const [blurred, setBlurred] = useState(false);

  const {
    result,
    filterActive,
    scorableItemType,
    listItems,
    titleFilterText,
    hasSelectedItems,
    dateRange,
    setScorableItemType,
    setTitleFilterText,
    setTitleFilters,
    getCheckboxState,
    setNodeCheckboxState,
    setScorableItemCheckboxState,
    setDateRange,
    selectAllItems,
    clearAllItems,
    clearFilters,
  } = useScorableItemFilters({
    nodeRowGroups,
  });

  const handleDateSelect = useCallback(
    (value: DateRange | undefined) => {
      setDateRange(value, scorableItemType);
    },
    [scorableItemType, setDateRange],
  );

  function handleTypeCheckboxChange(e: React.ChangeEvent<HTMLInputElement>, type: Exclude<ResultType, 'all'>) {
    if (scorableItemType === 'all') {
      if (!e.target.checked && type === 'free-exercise') setScorableItemType('assignment');
      else if (!e.target.checked && type === 'assignment') setScorableItemType('free-exercise');
    } else if (scorableItemType === 'assignment' || scorableItemType === 'free-exercise') {
      setScorableItemType(e.target.checked ? 'all' : 'none');
    } else {
      setScorableItemType(type);
    }
  }

  function handleClose() {
    closeModal(id);
  }

  function handleConfirmClick() {
    if (!hasSelectedItems) {
      setBlurred(true);
      return;
    }

    const filteredItems = result.map<FilteredItem>(id => {
      const [, isAssignmentString] = id.split('-');

      const isScorableItem = isAssignmentString === 'true' || isAssignmentString === 'false';

      return {
        type: isScorableItem ? 'scorable-item' : 'node',
        id,
      };
    });

    onConfirm(filteredItems);
    handleClose();
  }

  function renderDateRangeValue(range: DateRange) {
    return t('PAGES.RESULTS.MODALS.SCORABLE_ITEMS_FILTER.DATE_FIELD.VALUE', {
      from: dateFormatter.format(range.from),
      to: dateFormatter.format(range.to),
    });
  }

  const isEmpty = listItems.length === 0;

  return (
    <Dialog
      id={id}
      modal
      className="scorable-item-filter-modal"
      onClose={handleClose}
      size="sm"
      aria-label={t('PAGES.RESULTS.MODALS.SCORABLE_ITEMS_FILTER.A11Y_LABEL')}
    >
      <Dialog.Header
        title={t('PAGES.RESULTS.MODALS.SCORABLE_ITEMS_FILTER.TITLE')}
        onClose={handleClose}
        aria-label={t('ACTIONS.CLOSE')}
      >
        <div className="header-controls-grid">
          <SearchField
            className="header-controls-grid__search-field"
            value={titleFilterText}
            onClear={() => {
              setTitleFilterText('');
              setTitleFilters('');
            }}
            onSearch={() => setTitleFilters(titleFilterText)}
            onChange={e => setTitleFilterText(e.target.value)}
            placeholder={t('PAGES.RESULTS.MODALS.SCORABLE_ITEMS_FILTER.SEARCH_FIELD.PLACEHOLDER')}
            clearActionLabel={t('PAGES.RESULTS.MODALS.SCORABLE_ITEMS_FILTER.SEARCH_FIELD.CLEAR_A11Y_LABEL')}
            searchActionLabel={t('PAGES.RESULTS.MODALS.SCORABLE_ITEMS_FILTER.SEARCH_FIELD.BUTTON_TEXT')}
          />
          <div className="header-controls-grid__scorable-item-type-checkboxes">
            <Checkbox
              checked={['all', 'free-exercise'].includes(scorableItemType)}
              onChange={e => handleTypeCheckboxChange(e, 'free-exercise')}
            >
              {t('PAGES.RESULTS.MODALS.SCORABLE_ITEMS_FILTER.TYPE_SELECT.FREE-EXERCISE')}
            </Checkbox>
            <Checkbox
              checked={['all', 'assignment'].includes(scorableItemType)}
              onChange={e => handleTypeCheckboxChange(e, 'assignment')}
            >
              {t('PAGES.RESULTS.MODALS.SCORABLE_ITEMS_FILTER.TYPE_SELECT.ASSIGNMENT')}
            </Checkbox>
          </div>
          <LocalizedDateField
            mode="range"
            selected={dateRange}
            onSelect={handleDateSelect}
            aria-label={t('PAGES.RESULTS.MODALS.SCORABLE_ITEMS_FILTER.DATE_FIELD.A11Y_LABEL')}
            required
            placeholder={t('PAGES.RESULTS.MODALS.SCORABLE_ITEMS_FILTER.DATE_FIELD.PLACEHOLDER')}
            disabled={scorableItemType === 'none' || scorableItemType === 'free-exercise'}
            renderValue={renderDateRangeValue}
            dateMenuAriaLabel={t('PAGES.RESULTS.MODALS.SCORABLE_ITEMS_FILTER.DATE_FIELD.DATE_MENU.A11Y_LABEL')}
          />
        </div>
      </Dialog.Header>
      <Dialog.Body
        alertGroupProps={
          blurred && !hasSelectedItems
            ? {
                'aria-label': undefined,
                role: undefined,
                children: (
                  <Alert prominence="warning">
                    {t('PAGES.RESULTS.MODALS.SCORABLE_ITEMS_FILTER.NO_SELECTION.ALERT')}
                  </Alert>
                ),
              }
            : undefined
        }
      >
        {isEmpty ? (
          <Empty onClearFiltersClick={clearFilters} />
        ) : (
          <List className="scorable-items">
            {listItems?.map(listItem => (
              <NodeListItemGroup
                filterActive={filterActive}
                tocNodeRowGroup={listItem}
                key={listItem.id}
                getCheckboxState={getCheckboxState}
                setNodeCheckboxState={setNodeCheckboxState}
                setScorableItemCheckboxState={setScorableItemCheckboxState}
              />
            ))}
          </List>
        )}
      </Dialog.Body>
      <Dialog.Footer
        buttonGroupPropsLeft={{
          size: 'md',
          role: 'group',
          'aria-label': undefined,
          children: (
            <>
              <Button prominence="ghost" key="select-all" onClick={selectAllItems}>
                {t('ACTIONS.SELECT_ALL')}
              </Button>
              <Button prominence="ghost" key="clear-all" onClick={clearAllItems} disabled={!hasSelectedItems}>
                {t('ACTIONS.CLEAR_SELECTION')}
              </Button>
            </>
          ),
        }}
        buttonGroupPropsRight={{
          size: 'md',
          role: 'group',
          'aria-label': undefined,
          children: (
            <>
              <Button prominence="neutral" onClick={handleClose} key="cancel">
                {t('ACTIONS.CANCEL')}
              </Button>
              <Button prominence="success" onClick={handleConfirmClick} key="apply">
                {t('ACTIONS.APPLY')}
              </Button>
            </>
          ),
        }}
      />
    </Dialog>
  );
}
